/**
 * Box / vault statement (C19): the "كشف" button opens the filter dialog (old `openWalletFilterModal`,
 * App.tsx:9045-9083 → `WalletStatementFilterModal`), "عرض" opens the history dialog (`HistoryModal`,
 * App.tsx:50268-50920) over `GET /accounts/:id/statement` — every leg touching the account, running
 * balance from the ledger, dates in the office time zone (D04 §5 "Wallet statement", D-064). Presets
 * (today / yesterday / last 7 / this month / last month) are date arithmetic on the office clock
 * (`now()`), not money. Print = the A4 statement template of `@faylasuf/print` (first A4 print, B4)
 * through the browser's print dialog; "PDF" uses the same template with the browser's save-as-PDF.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { HistoryModal, WalletStatementFilterModal, formatWithCommas, type HistoryFilter, type HistoryRow, type WalletFilterPreset, type WalletFilterValues, type WalletStatementKind } from "@faylasuf/ui";
import { PRINT_ROOT_CLASS, StatementA4, printDocument } from "@faylasuf/print";
import type { AccountRow, StatementQuery, StatementRow } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { isSessionLost } from "../../app/errors.js";
import { keys } from "../../app/query.js";
import { usePrintOffice } from "../../app/printOffice.js";

const EMPTY_FILTER: WalletFilterValues = { preset: "all", startDate: "", endDate: "", operator: "", movementType: "" };

/** Office-date string YYYY-MM-DD of an instant in the office time zone. */
export function officeDate(d: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function shiftDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d! + days));
  return dt.toISOString().slice(0, 10);
}

function presetRange(preset: Exclude<WalletFilterPreset, "custom">, today: string): { startDate: string; endDate: string } {
  const [y, m] = today.split("-").map(Number);
  const monthStart = `${today.slice(0, 7)}-01`;
  switch (preset) {
    case "all":
      return { startDate: "", endDate: "" };
    case "today":
      return { startDate: today, endDate: today };
    case "yesterday": {
      const d = shiftDays(today, -1);
      return { startDate: d, endDate: d };
    }
    case "last7":
      return { startDate: shiftDays(today, -6), endDate: today };
    case "thisMonth":
      return { startDate: monthStart, endDate: today };
    case "lastMonth": {
      const prev = new Date(Date.UTC(y!, m! - 2, 1));
      const start = prev.toISOString().slice(0, 10);
      const end = shiftDays(monthStart, -1);
      return { startDate: start, endDate: end };
    }
  }
}

/** The history dialog's type filter → operation types of the gateway (one history "type" may cover several). */
const TYPE_GROUPS: Readonly<Record<string, readonly string[]>> = {
  deposit: ["quick_deposit", "capital_add", "usd_direct_deposit", "currency_direct_deposit", "customer_deposit", "opening_balance"],
  withdraw: ["quick_withdraw", "capital_withdraw", "usd_direct_withdraw", "currency_direct_withdraw", "customer_withdraw", "expense"],
  transfer: ["box_transfer", "custody_transfer", "custody_activate", "custody_deactivate", "transfer_out", "transfer_in", "transfer_out_settled", "transfer_in_settled", "transfer_currency_passthrough", "currency_transfer", "customer_transfer"],
  manual_work: ["manual_work"],
  trade: ["usd_buy", "usd_sell", "currency_buy", "currency_sell", "currency_sell_for_usd"],
  commission: ["partner_charge"],
};

function statementKind(a: AccountRow | undefined): WalletStatementKind {
  if (!a) return "digital_box";
  if (a.kind === "cash_vault") return a.currency === "USD" ? "dollar_vault" : "vault";
  if (a.kind === "custody") return a.currency === "USD" ? "custody_usd" : "custody_iqd";
  if (a.kind === "currency_position") return "currency_vault";
  return "digital_box";
}

export function StatementContainer({ accountId, accounts, onClose, initialStep = "filter" }: { accountId: string; accounts: readonly AccountRow[]; onClose: () => void; initialStep?: "filter" | "history" }) {
  const { source, t, locale, now } = useApp();
  const { session, sessionLost } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const tz = session.office.timeZone;
  const account = accounts.find((a) => a.id === accountId);
  const [step, setStep] = useState<"filter" | "history">(initialStep);
  const [values, setValues] = useState<WalletFilterValues>(EMPTY_FILTER);
  const [applied, setApplied] = useState<WalletFilterValues>(EMPTY_FILTER);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>({ type: "", from: "", to: "", operator: "", hour: "" });
  const [printing, setPrinting] = useState(false);
  const today = officeDate(now(), tz);

  const toQuery = (v: WalletFilterValues, h: HistoryFilter): StatementQuery => ({
    ...(h.from || v.startDate ? { from: h.from || v.startDate } : {}),
    ...(h.to || v.endDate ? { to: h.to || v.endDate } : {}),
    ...(v.operator ? { operatorUserId: v.operator } : {}),
    ...(h.type === "incoming" ? { direction: "in" as const } : h.type === "outgoing" ? { direction: "out" as const } : {}),
    limit: 2000,
  });

  // Unfiltered: operators and counts for the filter dialog; filtered: the rows shown.
  const allQuery = useQuery({ queryKey: keys.statement(accountId, "all"), queryFn: () => source.statement(accountId, { limit: 2000 }) });
  const query = toQuery(applied, historyFilter);
  const filteredQuery = useQuery({ queryKey: keys.statement(accountId, query), queryFn: () => source.statement(accountId, query), enabled: step === "history" });
  if ((allQuery.error && isSessionLost(allQuery.error)) || (filteredQuery.error && isSessionLost(filteredQuery.error))) sessionLost();

  const typeGroup = (v: WalletFilterValues, h: HistoryFilter): readonly string[] | null => {
    const key = h.type && h.type !== "incoming" && h.type !== "outgoing" ? h.type : v.movementType && v.movementType !== "incoming" && v.movementType !== "outgoing" ? v.movementType : "";
    return key ? (TYPE_GROUPS[key] ?? null) : null;
  };
  const directionOf = (v: WalletFilterValues): "in" | "out" | null => (v.movementType === "incoming" ? "in" : v.movementType === "outgoing" ? "out" : null);

  const operators = useMemo(() => {
    const seen = new Map<string, { id: string; label: string; count: number }>();
    for (const r of allQuery.data?.rows ?? []) {
      const cur = seen.get(r.actorUserId) ?? { id: r.actorUserId, label: r.actorUsername ?? r.actorUserId.slice(0, 8), count: 0 };
      cur.count += 1;
      seen.set(r.actorUserId, cur);
    }
    return [...seen.values()];
  }, [allQuery.data]);

  // Counts of the filter dialog (D-064: office dates), computed over the unfiltered rows (counting, not money).
  const matching = useMemo(() => {
    const rows = allQuery.data?.rows ?? [];
    const group = typeGroup(values, { type: "", from: "", to: "", operator: "", hour: "" });
    const dir = directionOf(values);
    return rows.filter((r) => (!values.startDate || r.officeDate >= values.startDate) && (!values.endDate || r.officeDate <= values.endDate) && (!values.operator || r.actorUserId === values.operator) && (!group || group.includes(r.type)) && (!dir || (dir === "in" ? r.amount.startsWith("-") === false && r.amount !== "0" : r.amount.startsWith("-"))));
  }, [allQuery.data, values]);
  const sumAbs = (rows: readonly StatementRow[], sign: "in" | "out"): string => {
    let total = 0n;
    for (const r of rows) {
      const neg = r.amount.startsWith("-");
      if ((sign === "in" && !neg) || (sign === "out" && neg)) total += BigInt(r.amount.replace("-", "").split(".")[0]!);
    }
    return formatWithCommas(total.toString());
  };

  const unit = account?.currency === "USD" ? "$" : t("unit.iqd");
  const rowsShown: HistoryRow[] = useMemo(() => {
    const list = filteredQuery.data?.rows ?? [];
    const group = typeGroup(applied, historyFilter);
    const dir = directionOf(applied);
    return list
      .filter((r) => (!group || group.includes(r.type)) && (!dir || (dir === "in" ? !r.amount.startsWith("-") : r.amount.startsWith("-"))) && (!historyFilter.hour || hourOf(r.postedAt, tz) === historyFilter.hour))
      .map((r): HistoryRow => {
        const neg = r.amount.startsWith("-");
        const abs = r.amount.replace("-", "");
        const typeLabel = t(`optype.${r.type}`);
        return {
          id: r.operationId,
          dateLabel: r.officeDate,
          timeLabel: new Date(r.postedAt).toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", timeZone: tz }),
          typeKey: neg ? "outgoing" : "incoming",
          typeLabel: typeLabel === `optype.${r.type}` ? r.type : typeLabel,
          badgeTone: neg ? "rose" : "emerald",
          description: r.counterparties.join(" / ") || "-",
          note: r.note ?? "-",
          operator: r.actorUsername ?? "",
          inLabel: neg ? "-" : `${formatWithCommas(abs)} ${unit}`,
          outLabel: neg ? `${formatWithCommas(abs)} ${unit}` : "-",
          commissionLabel: "-",
          balanceLabel: `${formatWithCommas(r.runningBalance)} ${unit}`,
          balanceUsdLabel: "",
        };
      });
  }, [filteredQuery.data, applied, historyFilter, t, tz, unit]);

  const title = account?.name ?? t("vault");
  const balanceLabel = `${formatWithCommas(account?.kind === "cash_vault" ? (account.total ?? "0") : (account?.balance ?? "0"))} ${unit}`;

  // The template mounts under <body> (portal: the print rule hides every other top-level node), then the
  // browser's print dialog opens; `afterprint` unmounts it.
  useEffect(() => {
    if (!printing) return;
    const done = () => setPrinting(false);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printing]);
  const print = () => setPrinting(true);

  return (
    <>
      {step === "filter" && (
        <WalletStatementFilterModal
          kind={statementKind(account)}
          walletName={title}
          balanceLabel={balanceLabel}
          operators={operators}
          values={values}
          onChange={(patch) => setValues((v) => ({ ...v, ...patch, ...(patch.startDate !== undefined || patch.endDate !== undefined ? { preset: "custom" as const } : {}) }))}
          onPresetSelect={(preset) => setValues((v) => ({ ...v, preset, ...presetRange(preset, today) }))}
          onReset={() => setValues(EMPTY_FILTER)}
          matchingCount={matching.length}
          totalCount={allQuery.data?.rows.length ?? 0}
          totalInLabel={`${sumAbs(matching, "in")} ${unit}`}
          totalOutLabel={`${sumAbs(matching, "out")} ${unit}`}
          onApply={() => {
            setApplied(values);
            setHistoryFilter({ type: values.movementType, from: values.startDate, to: values.endDate, operator: values.operator, hour: "" });
            setStep("history");
          }}
          onViewAll={() => {
            setValues(EMPTY_FILTER);
            setApplied(EMPTY_FILTER);
            setHistoryFilter({ type: "", from: "", to: "", operator: "", hour: "" });
            setStep("history");
          }}
          onClose={onClose}
        />
      )}
      {step === "history" && (
        <HistoryModal
          variant="account"
          title={title}
          rows={rowsShown}
          filter={historyFilter}
          onFilterChange={(patch) => setHistoryFilter((f) => ({ ...f, ...patch }))}
          onOpenAdvancedFilter={() => setStep("filter")}
          onRowDetails={() => undefined}
          onPrint={print}
          onExportPdf={print}
          onClose={onClose}
          loading={filteredQuery.isLoading}
          emptyState={(allQuery.data?.rows.length ?? 0) === 0 ? "no_movements" : "no_match"}
        />
      )}
      {printing &&
        createPortal(
        <div className={PRINT_ROOT_CLASS} data-testid="print-root">
          <StatementA4
            locale={locale}
            title={`${t("history.account_title")}: ${title}`}
            dateLabel={today}
            docNumber={String(rowsShown[0]?.id.slice(-6) ?? "000000")}
            office={printOffice}
            columns={[
              { header: t("date"), key: "date" },
              { header: t("history.category"), key: "category" },
              { header: t("description"), key: "description" },
              { header: t("note"), key: "note" },
              { header: t("incoming"), key: "incoming" },
              { header: t("outgoing"), key: "outgoing" },
              { header: t("history.balance"), key: "balance" },
            ]}
            rows={rowsShown.map((r) => ({ date: `${r.dateLabel} ${r.timeLabel}`, category: r.typeLabel, description: r.description, note: r.note, incoming: r.inLabel, outgoing: r.outLabel, balance: r.balanceLabel }))}
            summary={[
              { label: t("print.total_in"), value: `${sumAbs(filteredQuery.data?.rows ?? [], "in")} ${unit}` },
              { label: t("print.total_out"), value: `${sumAbs(filteredQuery.data?.rows ?? [], "out")} ${unit}` },
              { label: t("print.closing_balance"), value: `${formatWithCommas(filteredQuery.data?.totals.closing ?? "0")} ${unit}` },
            ]}
          />
        </div>,
          document.body,
        )}
    </>
  );
}

function hourOf(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", hour12: false }).format(new Date(iso)).slice(0, 2);
}
